import Vue from 'vue';
import store from '@/store';

// Same breakpoint the login pages use to tell a phone from a desktop.
const PHONE_MAX_WIDTH = 768;

// localStorage is not reactive. Bumping `version` on every write re-runs any
// computed that resolved the participation, so a choice made on one screen
// reaches every other screen at once.
const participationState = Vue.observable({ version: 0 });

// Per-user participation key — drives reactive consumers like
// LiveStream's `userParticipationType` getter and the lobby gating
// logic that depends on it.
function participationStorageKey(userId) {
  return `tappin_participation_${userId || 'guest'}`;
}

function setStoredParticipationType(userId, type) {
  if (type !== 'physical' && type !== 'digital') return;
  try {
    localStorage.setItem(participationStorageKey(userId), type);
  } catch (e) {
    /* localStorage unavailable — silent no-op */
  }
  participationState.version += 1;
}

function getStoredParticipationType(userId) {
  try {
    return localStorage.getItem(participationStorageKey(userId));
  } catch (e) {
    return null;
  }
}

// Map the boolean Login form `platform` (false = physical,
// true = digital) into the canonical 'physical' / 'digital' string
// that the rest of the platform stores in localStorage.
function participationFromPlatformBoolean(platform) {
  if (platform === true) return 'digital';
  if (platform === false) return 'physical';
  return null;
}

function isPhoneViewport() {
  return typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH;
}

// Hybrid = the web app plus at least one digital page (lobby or stage). Only
// then does the attendee have a physical / digital choice to make.
function isHybridEvent() {
  const { getters } = store;
  return (
    getters['CurrentEvent/isWebappEnabled'] &&
    (getters['CurrentEvent/isLobbyEnabled'] ||
      getters['CurrentEvent/isStageEnabled'])
  );
}

// Whether the signed-in attendee is on the web app ('physical') or the
// digital platform ('digital'). Single source of truth for every screen,
// following the pre-WCAG rules:
//   - web app is the only platform page -> physical, whatever was stored
//   - no web app                        -> digital, whatever was stored
//   - hybrid -> the attendee's stored choice; with none stored a phone is
//     physical (phone logins always went to the web app) and a desktop is
//     digital.
// A stored choice is ignored outside hybrid events, so a value left over
// from an earlier event setup can no longer flip the attendee's platform.
function resolveParticipationType() {
  // eslint-disable-next-line no-unused-vars
  const _ = participationState.version; // touch to force reactivity
  const { getters } = store;

  if (!getters['CurrentEvent/isWebappEnabled']) return 'digital';
  if (!isHybridEvent()) return 'physical';

  const user = getters['Authentication/getUser'];
  const stored = getStoredParticipationType(user && user.UserID);
  if (stored === 'physical' || stored === 'digital') return stored;

  return isPhoneViewport() ? 'physical' : 'digital';
}

// A physically-attending user is at the venue, so the lobby (the online
// "event info" surface) is not for them — it must stay hidden even when the
// admin has the lobby toggle ON.
function isPhysicalAttendee() {
  return resolveParticipationType() === 'physical';
}

// Phones never show the physical / online picker. As before WCAG, a phone
// login on a hybrid event is a web app (physical) attendee, so record that
// instead of keeping whatever an earlier session stored. Returns the login
// form `platform` value to use (false = physical).
function phoneLoginPlatform(platform) {
  if (platform === null && isPhoneViewport() && isHybridEvent()) return false;
  return platform;
}

// Every $router.push here is fire-and-forget: nothing awaits it. In vue-router 3 a push to the
// route you are already on REJECTS with NavigationDuplicated, so an uncaught push surfaces as an
// unhandled promise rejection. That is guaranteed on an event whose only enabled platform page is
// the web app, because 'webapp' is then both the current route and the redirect target.
// The .catch keeps a redundant redirect a no-op instead of an error.
function safePush($router, location) {
  return $router.push(location).catch(() => {});
}

// The destination an authenticated user belongs on, given the event's enabled platform pages.
// Lobby visibility = admin's "Lobby" toggle (`isLobbyEnabled`) AND the
// attendee is NOT attending physically. A physical attendee never lands
// on the lobby even when the admin has it ON — they go straight to the
// program. Digital attendees still land on the lobby when it's enabled;
// when it's OFF, everyone goes to the program page.
function authenticatedLandingRoute(
  isWebappEnabled,
  isLobbyEnabled,
  isStageEnabled
) {
  const isPhysical = isPhysicalAttendee();

  if (isLobbyEnabled && !isPhysical) return 'lobby';
  if (isWebappEnabled && !(isLobbyEnabled || isStageEnabled)) return 'webapp';
  return 'program';
}

function redirectAuthenticatedUser(
  $router,
  isWebappEnabled,
  isLobbyEnabled,
  isStageEnabled,
  platform,
  pollAccessKey,
  type
) {
  safePush($router, {
    name: authenticatedLandingRoute(
      isWebappEnabled,
      isLobbyEnabled,
      isStageEnabled
    )
  });
  if (type === 0) {
    if (pollAccessKey) {
      safePush($router, {
        name: 'poll-results',
        params: { pollAccessKey: pollAccessKey }
      });
    } else {
      safePush($router, {
        name: 'generic-poll-results'
      });
    }
  }
  if (type === 1) {
    safePush($router, {
      name: 'participants-status'
    });
  }

  return $router;
}

function redirectAuthenticatedUserToResults($router, pollAccessKey, type) {
  if (type === 0) {
    const checkIfTemplate = window.location.pathname.split('/')[2];
    if (pollAccessKey) {
      if (checkIfTemplate.startsWith('template')) {
        safePush($router, {
          name: 'poll-template-results',
          params: { pollAccessKey: pollAccessKey }
        });
        return;
      }

      safePush($router, {
        name: 'poll-results',
        params: { pollAccessKey: pollAccessKey }
      });
    } else {
      safePush($router, {
        name: 'generic-poll-results'
      });
    }
  }
  if (type === 1) {
    safePush($router, {
      name: 'participants-admitted'
    });
  }

  return $router;
}

export {
  redirectAuthenticatedUser,
  redirectAuthenticatedUserToResults,
  authenticatedLandingRoute,
  setStoredParticipationType,
  getStoredParticipationType,
  participationFromPlatformBoolean,
  isPhysicalAttendee,
  isHybridEvent,
  resolveParticipationType,
  phoneLoginPlatform
};
